import axios from 'axios';
import {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import Grid from '@mui/material/Grid';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';

import Loading from '../../common/Loading';
import Error from '../../common/Error';
import EditStatus from '../../common/EditStatus';
import CustomerReelsEditReelTypeLookup from './CustomerReelsEditReelTypeLookup';

import { useSessionContext } from '../../../contexts/SessionContext';

export default function CustomerReelsEdit(props) {
    //console.debug("RTS Break CustomerReelsEdit");

    const theme = useTheme();
    const smallForm = useMediaQuery(theme.breakpoints.down("sm"));

    const {user, sessionCustomer, userAuthToken, apiRoot} = useSessionContext();

    const history = useNavigate();
    //const display = props.display;
    const mode = props.mode;
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorBody, setErrorBody] = useState("");
    const [errorTitle, setErrorTitle] = useState("Validation Error");
    const [showReelTypeLookup, setShowReelTypeLookup] = useState(false);
    const [removeReel, setRemoveReel] = useState(null);

    const [idGuid, setIdGuid] = useState("00000000-0000-0000-0000-000000000000");
    const [reelType, setReelType] = useState(null);
    const [status, setStatus] = useState(1);
    const [serialNumber, setSerialNumber] = useState('');
     
    const fetchData = useCallback((idGuid) => {
        let apiURL = apiRoot + "/Reels/GetReelById/" + idGuid;
        axios
        .get(
            apiURL,
            {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
        )
        .then(response => {
            if (response.data) {
                setIsLoading(false);

                setIdGuid(response.data.idGuid)
                setReelType(response.data.reelType);
                setStatus(response.data.status);
                setSerialNumber(response.data.serialNumber!==null?response.data.serialNumber:'');
            }
        })
        .catch(error => {
            setHasError(true);
            console.log("RTS Data Error", error);
        });
    },[apiRoot, userAuthToken.token]);

    
    useEffect(() => {
        if(mode === 1){
            fetchData(props.idGuid);
        }
        else{
            setIsLoading(false);
        }

    },[mode, fetchData, props.idGuid]);  


    async function handleSubmit(e) {
        e.preventDefault();

        if(serialNumber === null){
            setErrorTitle("Validation Error");
            setErrorBody("Serial Number is required");
            setShowErrorModal(true);
            return;
        }

        if(reelType === null){
            setErrorTitle("Validation Error");
            setErrorBody("Reel Type is required");
            setShowErrorModal(true);
            return;
        }

        let serialNumberCheck = await validateSerialNumber();
        if(serialNumberCheck === -1){
            return;
        }

        saveData();
    };


    function validateSerialNumber(){
        return new Promise(function(resolve, reject){
            let apiURL = apiRoot + "/Reels/GetReelBySerialNumber/" + serialNumber
            axios
            .get(
                apiURL,
                {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
            )
            .then(response => {
                if (response.data) {
                    if(response.data.idGuid && response.data.idGuid.length > 0 && response.data.idGuid !== idGuid){
                        setErrorTitle("Validation Error");
                        setErrorBody("A Reel with Serial Number " + serialNumber + " already exists");
                        console.log("RTS Error", "Reel already exists");
                        setShowErrorModal(true);
                        resolve(-1);
                    }
                    else{
                        resolve(0);
                    }                    
                }
                else{
                    resolve(0);
                }
            })
            .catch(error => {
                setErrorTitle("Validation Error");
                setErrorBody("An error has occured.  Please review the console log for details.");
                console.log("RTS Tracker Validation Error", error);
                setShowErrorModal(true);
                reject(-1);
            });
        });
    };


    function saveData(){
        let apiObj = {
            IdGuid: idGuid,
            Customer: sessionCustomer.customer,
            IdSap: null,
            SerialNumber: serialNumber===''?null:serialNumber,
            ReelType: reelType,
            Tracker: null,
            Status: status,
            modifyUser: user.userName
        }

        if(mode > 0){
            let apiURL = apiRoot + "/Reels/UpdateReel";
            axios
            .put(
                apiURL,
                apiObj, 
                {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
            )
            .then(response => {
                history(-1);
            })
            .catch(error => {
                setErrorTitle("Update Error");
                setErrorBody("An error has occured.  Please review the console log for details.");
                console.log("RTS UpdateReelType Error", error);
                setShowErrorModal(true);
            });
        }
        else{
            apiObj.createUser = user.userName;
            let apiURL = apiRoot + "/Reels/CreateReel";
            axios
            .post(
                apiURL,
                apiObj, 
                {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
            )
            .then(response => {
                history(-1);
            })
            .catch(error => {
                setErrorTitle("Insert Error");
                setErrorBody("An error has occured.  Please review the console log for details.");
                console.log("RTS CreateReelType Error", error);
                setShowErrorModal(true);
            });
        }
    }; 


    function selectReelType(val){
        setShowReelTypeLookup(false);
        setReelType(val);
    };


    function clearReelType(){
        setShowReelTypeLookup(false);
        setReelType(null);
    }    


    if(hasError){
        return(
            <Error />
        );
    };


    if(isLoading){
        return(
            <Loading />
        );
    };    


    return (
        <>
        <Dialog open={showErrorModal} onClose={()=>{setShowErrorModal(false);}}>
            <DialogTitle>{errorTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText>{errorBody}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={e => {setShowErrorModal(false);}}>Close</Button>
            </DialogActions>
        </Dialog>
        <Dialog open={showReelTypeLookup} fullWidth={true} maxWidth="sm" onClose={()=>{setShowReelTypeLookup(false);}}>
            <DialogTitle>Select Reel Type</DialogTitle>
            <DialogContent>
                <CustomerReelsEditReelTypeLookup reelType={reelType} setReelTypeFx={selectReelType} clearReelTypeFx={clearReelType} setFormDirtyFx={props.setFormDirtyFx} />
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={e => {setShowReelTypeLookup(false);}}>Close</Button>
            </DialogActions>
        </Dialog>        
        <Box>
            <form id="reelForm" onSubmit={handleSubmit} >
                <Box id="mdBox" maxWidth="md" sx={{display:'flex', alignItems:'center', justifyContent:'center'}}>
                <Box id="smBox" maxWidth="sm" sx={{width:smallForm?'auto':'600px'}}>
                <TextField
                    id="tbSerialNumber"
                    margin="normal"
                    fullWidth
                    label="Serial Number"
                    size="small"
                    required
                    value={serialNumber}
                    onChange={(e) => {props.setFormDirtyFx(1); setSerialNumber(e.target.value);}}
                />                                               
                <Box sx={{display:'flex', alignItems:'center', width:'100%'}}>
                    <TextField
                        id="tbReelType"
                        margin="normal"
                        fullWidth
                        label="ReelType"
                        size="small"
                        required
                        value={reelType==null?'':reelType.name}
                        onClick={setShowReelTypeLookup}
                    />
                    <Box sx={{ml:1, mt:1}}>            
                        <Button variant="contained" onClick={setShowReelTypeLookup}>
                            <SearchIcon sx={{fontSize:'2em'}} />
                        </Button>
                    </Box>            
                </Box>
                <EditStatus status={status} setStatusFx={setStatus} setFormDirtyFx={props.setFormDirtyFx} /> 
                <Box sx={{textAlign:'center', mt:3}}>
                    <Button variant="contained" disabled={props.formDirty===1?false:true} type="submit">Save</Button>
                </Box>
                </Box>
                </Box>
            </form>
        </Box>
        </>
    );
}
